import React, { useState } from 'react';

import { ActionIcon, Button, MultiSelect, Select, Title, Tooltip, createStyles } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFilterX } from '@tabler/icons-react';
import DownloadCSV from 'components/DownloadCSV';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import { MRT_PaginationState, MRT_RowSelectionState, MRT_SortingState } from 'mantine-react-table';
import { AthleteEvent } from 'types';
import { getDayFromDate } from 'utils/event.helpers';

import { ReservationType, SearchFilters, WeekdayType } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto auto min-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title weekday reservationType dateRange eventTypes delete clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title clear download'
        'dateRange eventTypes eventTypes eventTypes'
        'weekday reservationType reservationType delete'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto auto min-content min-content',
      gridTemplateRows: 'repeat(auto, 4)',
      gridTemplateAreas: `
        'title title clear download'
        'dateRange dateRange dateRange dateRange'
        'weekday weekday weekday weekday'
        'reservationType reservationType reservationType reservationType'
        'eventTypes eventTypes eventTypes delete'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  reservationType: {
    gridArea: 'reservationType',
  },
  weekday: {
    gridArea: 'weekday',
  },
  dateRange: {
    gridArea: 'dateRange',
    minWidth: '16rem',
  },
  eventTypes: {
    gridArea: 'eventTypes',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
  },
  delete: {
    gridArea: 'delete',
  },
}));

const Weekdays: Array<{ label: WeekdayType['weekDay']; value: WeekdayType['day'] }> = [
  { value: '1', label: 'Sunday' },
  { value: '2', label: 'Monday' },
  { value: '3', label: 'Tuesday' },
  { value: '4', label: 'Wednesday' },
  { value: '5', label: 'Thursday' },
  { value: '6', label: 'Friday' },
  { value: '7', label: 'Saturday' },
];

const Reservations: Array<{ label: ReservationType['label']; value: ReservationType['type'] }> = [
  { value: 'true', label: 'Bulk Add' },
  { value: 'false', label: 'Not Bulk Add' },
];

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: false,
  headers: ['Name', 'Date', 'Time', 'Reserved', 'Attended', 'Weekday', 'Bulk Added', 'Credited'],
};

const today = new Date();

interface IAthleteReservationsFiltersProps {
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  initialSearchFiltersValue: SearchFilters;
  handleDeleteBulkAthleteEvents: () => void;
  rowSelection: MRT_RowSelectionState;
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
}

const AthleteReservationsFilters = ({
  searchFilters,
  setSearchFilters,
  initialSearchFiltersValue,
  pagination,
  sorting,
  handleDeleteBulkAthleteEvents,
  rowSelection,
}: IAthleteReservationsFiltersProps): JSX.Element => {
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();

  const [eventTypes, setEventTypes] = useState<IEventTypeMultiSelectAutoComplete[]>([]);

  const dataToRow = (d: AthleteEvent): { [key: string]: any } => ({
    Name: d.name,
    Date: dayjs(d.startDate).format(dateFormat),
    Time: dayjs(`${d.startDate} ${d.startTime}`).format('h:mm A'),
    Reserved: d.isReserved ? 'Yes' : 'No',
    Attended: d.isCheckedIn && dayjs(`${d.startDate} ${d.startTime}`).isBefore(dayjs(today)) ? 'Yes' : 'No',
    Weekday: getDayFromDate(d.startDate),
    'Bulk Added': d.isBulkAdd,
    Credited: d.isCredited,
  });

  const handleDateChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, date: value }));
  };
  const handleEventsChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setEventTypes(values);
    setSearchFilters(prev => ({ ...prev, events: values.map(v => v.eventTypeId) }));
  };

  const handleWeekdaysChange = (values: WeekdayType['day'][]): void => {
    setSearchFilters(prev => ({ ...prev, weekdays: values }));
  };

  const handleReservationTypeChange = (value: ReservationType['type'] | null): void => {
    setSearchFilters(prev => ({ ...prev, isBulkAdd: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
    setEventTypes([]);
  };

  return (
    <div className={classes.header}>
      <Title order={2} className={classes.title}>
        Reservations
      </Title>
      <MultiSelect
        value={searchFilters.weekdays}
        onChange={event => handleWeekdaysChange(event as WeekdayType['day'][])}
        data={Weekdays}
        className={classes.weekday}
        placeholder='Select weekdays'
      />
      <Select
        value={searchFilters.isBulkAdd}
        onChange={event => handleReservationTypeChange(event as ReservationType['type'])}
        data={Reservations}
        className={classes.reservationType}
        placeholder='Select reservation type'
        clearable
      />
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.date}
        onChange={handleDateChange}
        valueFormat='YYYY MMM DD'
        type='range'
        allowSingleDateInRange
        clearable
        {...{ placeholder: 'Select date range' }}
        minDate={today}
      />
      <EventTypeMultiSelectAutoComplete
        className={classes.eventTypes}
        handleChange={handleEventsChange}
        values={eventTypes}
      />
      <Button
        className={classes.delete}
        onClick={handleDeleteBulkAthleteEvents}
        disabled={!Object.keys(rowSelection).length}
      >
        Delete
      </Button>
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterX />
        </ActionIcon>
      </Tooltip>
      <DownloadCSV
        useList={useAthleteEvents}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{
          ...searchFilters,
          startDateRange:
            searchFilters.date && searchFilters.date[0]
              ? dayjs(searchFilters.date[0]).format('YYYY-MM-DD')
              : dayjs(today).format('YYYY-MM-DD'),
          endDateRange:
            searchFilters.date && searchFilters.date[1]
              ? dayjs(searchFilters.date[1]).format('YYYY-MM-DD')
              : undefined,
          hideWaitlist: false,
          ordering: sorting[0].desc ? sorting[0].id : `-${sorting[0].id}`,
          isBulkAdd: !!searchFilters.isBulkAdd ? searchFilters.isBulkAdd === 'true' : undefined,
          type: 'upcoming',
          offset: `${pagination.pageIndex * pagination.pageSize}`,
        }}
        limit={'100'}
        className={classes.download}
        reportType='athlete-reservations-export'
      />
    </div>
  );
};

export default AthleteReservationsFilters;
